import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

/**
 * Keeping Ask's transcript at the bottom (日本-488).
 *
 * **The reader's own scroll gesture is the only thing that un-pins.** That is the whole design, and
 * it is the inversion of what shipped first. The original followed the answer by measuring the box
 * *after* the content had already grown — `scrollHeight - scrollTop - clientHeight < 40` — which
 * structurally cannot tell "the reader scrolled away" apart from "the content got taller". Once a
 * single frame adds more than the threshold, the follow reads as a reader who has scrolled up, and
 * it never engages again for the rest of the answer.
 *
 * That was not a marginal failure. Measured in a browser against an 18-message thread: opening the
 * segment sat **2136px** from the bottom, and an answer streamed from a pinned start of exactly 0
 * went 417 → 551 → 599 → **616** and froze — the first frame alone (the question row plus a pending
 * tool line) is 417px, so the follow was already off before the first token of prose landed.
 *
 * So the distance is read on the **scroll event**, where it is a statement about a gesture, and
 * never after a mutation, where it is a statement about the content. A programmatic scroll fires
 * the same handler and lands at ~0, so it re-pins itself rather than fighting.
 */

/**
 * How close to the bottom still counts as being at the bottom.
 *
 * Comfortably under one wheel notch (~100px in Chromium), so nudging up by one really does detach;
 * comfortably over the sub-pixel residue a fractional `scrollTop` leaves under zoom or a
 * non-integer device pixel ratio, which would otherwise un-pin a transcript visibly at its end.
 */
export const PIN_THRESHOLD = 48;

export interface ScrollMetrics {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
}

/**
 * How far the box is from its own bottom, floored at zero.
 *
 * The floor is not cosmetic: a trackpad's rubber-band overscroll reports a `scrollTop` past the
 * end, so the raw subtraction goes negative — and a negative distance is *more* at the bottom than
 * zero is, never less.
 */
export function distanceFromBottom(metrics: ScrollMetrics): number {
  const { scrollTop, scrollHeight, clientHeight } = metrics;
  return Math.max(0, scrollHeight - scrollTop - clientHeight);
}

/** Whether the box is close enough to its end to count as being at it. */
export function atBottom(metrics: ScrollMetrics, threshold = PIN_THRESHOLD): boolean {
  return distanceFromBottom(metrics) < threshold;
}

export interface PinState {
  /** Whether the transcript is currently following its own end. */
  pinned: boolean;
  /** The last `scrollTop` this hook is responsible for; -1 before it has written one. */
  written: number;
}

/**
 * What one scroll position means.
 *
 * **Not every scroll is a reader**, and that is the rule this whole module turns on. It is asked
 * both when a scroll event arrives and again before every correction, because a scroll event is
 * delivered too late to be the only signal — see `stick`. It was got wrong twice while 日本-488
 * was being fixed, both times by reading the browser's own doing as a gesture:
 *
 * - a `scroll` is delivered *asynchronously*, so an event queued by this hook's own write arrives
 *   after the answer has streamed on — the same `scrollTop`, a larger `scrollHeight`, and a gap
 *   that reads exactly like somebody having scrolled up;
 * - and when the composer's height changes underneath it — reference chips arriving, the
 *   suggestion row coming back as the draft clears — **the browser moves `scrollTop` itself** to
 *   hold the content still. Traced in a real browser: a 348 → 316px composer bumped `scrollTop`
 *   4958 → 4990 with nobody touching anything, and the transcript detached for the rest of the
 *   answer.
 *
 * Three rules, in order:
 *
 * 1. **At the end is pinned**, however it got there. This is a state, not a history.
 * 2. **Moving toward the end is never a reader scrolling away.** Both cases above land here — the
 *    browser only ever bumps `scrollTop` *up*, toward the bottom — so a position at or past what
 *    this hook last wrote leaves the decision exactly as it was.
 * 3. **Anything else is a reader scrolling up**, and un-pins.
 *
 * Deliberately not a "this one was programmatic" flag held over a timeout: a timeout has to guess
 * how long an echo takes, and a guess that is too short is the first bug above while one that is
 * too long swallows a real gesture.
 */
export function pinAfterScroll(
  metrics: ScrollMetrics,
  state: PinState,
  threshold = PIN_THRESHOLD,
): boolean {
  if (atBottom(metrics, threshold)) return true;
  if (metrics.scrollTop >= state.written) return state.pinned;
  return false;
}

export interface StickToBottom {
  /** The scrolling box. */
  scrollRef: (node: HTMLElement | null) => void;
  /** The single wrapper holding every row — what is measured for growth. */
  contentRef: (node: HTMLElement | null) => void;
  /** Wire to the scrolling box's `onScroll`. */
  onScroll: () => void;
  /** Declare an intent to be at the bottom, whatever the reader was re-reading. */
  pin: () => void;
}

/**
 * Stick a scrolling box to its own bottom until the reader scrolls away from it.
 *
 * **Two mechanisms, and each covers what the other cannot.**
 *
 * A **layout effect on every render** handles everything React causes — a streamed delta, a card
 * laying out, reference chips changing the composer's height underneath. It runs after the DOM is
 * mutated and before the browser paints, so there is no frame in which the transcript is behind.
 *
 * A **`ResizeObserver`** handles growth React did not cause: a webfont swapping in under a long
 * answer, an image, anything that changes height without a render. It also delivers an initial
 * observation on `observe`, which is a convenience rather than the mount path — the layout effect
 * has already run by then.
 *
 * The observer is not enough on its own, and that is measured rather than assumed. The scroll box
 * and the wrapper inside it are at different depths, so a commit that resizes both makes
 * `ResizeObserver` broadcast the shallower one, defer the deeper one to the next frame, and
 * **paint in between**: up to 97px behind for a frame or two, on about half of all runs at 390px.
 *
 * **Neither is keyed on a dependency list, deliberately.** `useAsk` rebuilds `rows` as
 * `[...stored, ...live.rows]`, so it has a fresh identity on every render of the surface above — an
 * effect keyed on it re-runs on every hover, focus change and capture in the log beside it, while
 * *still* missing every height change that is not a new row. The whole cost of running anyway is
 * one conditional `scrollTop` write, which is a no-op when the transcript is already at its end.
 *
 * **`scrollTop` is assigned rather than a sentinel `scrollIntoView`d.** `scrollIntoView` walks
 * every scrollable ancestor, and on a phone this transcript is nested inside the bottom sheet's own
 * scroller (`BottomSheet.tsx`) — so following an answer could drag the sheet out from under the
 * reader.
 */
export function useStickToBottom(threshold = PIN_THRESHOLD): StickToBottom {
  const box = useRef<HTMLElement | null>(null);
  const content = useRef<HTMLElement | null>(null);
  const pinned = useRef(true);
  const observer = useRef<ResizeObserver | null>(null);
  const written = useRef(-1);

  /**
   * Go to the end now, and remember where that put us.
   *
   * Setting `scrollTop` past the maximum is clamped by the browser, so there is no arithmetic here
   * to get wrong. It is read back rather than assumed: the clamp is to a fraction under zoom, and
   * what the rule has to recognise later is the value that actually landed.
   */
  const toBottom = useCallback(() => {
    const node = box.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
    written.current = node.scrollTop;
  }, []);

  /** Correct the position, but only if the rule still says the transcript is following its end. */
  const stick = useCallback(() => {
    const node = box.current;
    if (!node) return;
    // **The rule is re-read here, not trusted from the last scroll event.** A scroll is delivered
    // asynchronously, and during a stream a delta renders several times a second — so a reader who
    // scrolls up mid-answer is very likely to have the next correction run *before* their scroll
    // event arrives. Trusting the flag put them straight back at the bottom, and the event,
    // arriving to find the box at its end, then agreed: the gesture erased by the code written
    // to respect it.
    pinned.current = pinAfterScroll(
      node,
      { pinned: pinned.current, written: written.current },
      threshold,
    );
    if (pinned.current) toBottom();
  }, [threshold, toBottom]);

  // **Every render, before the browser paints.** The observer alone leaves a visible frame behind:
  // the scroll box and the wrapper inside it are at different depths, and when a commit resizes
  // both — an answer growing while the composer's suggestion row returns underneath it —
  // `ResizeObserver` broadcasts the shallower one, defers the deeper one to the next frame, and
  // **paints in between**. Measured on painted frames at 390px: up to 97px behind for a frame or
  // two, on about half of all runs, with the observer working exactly as documented.
  //
  // A layout effect runs synchronously after the DOM is mutated and before paint, so anything
  // React caused is corrected with no frame to see. No dependency list on purpose: the work is one
  // conditional `scrollTop` write, which is a no-op when the transcript is already at its end, and
  // any dependency precise enough to skip those renders is the `[rows, busy]` guess this module
  // exists to be rid of. The observer stays for growth React did not cause — a webfont swapping in
  // under a long answer.
  useLayoutEffect(stick);

  useEffect(() => {
    // Built once and re-targeted by the two callback refs, rather than rebuilt per element: a
    // component that re-mounts its scroller would otherwise leave the old observer live.
    const watch = new ResizeObserver(stick);
    observer.current = watch;
    if (box.current) watch.observe(box.current);
    if (content.current) watch.observe(content.current);
    return () => {
      watch.disconnect();
      observer.current = null;
    };
  }, [stick]);

  // Callback refs rather than object refs, so a re-mounted scroller is re-observed. They fire
  // during commit, *before* the effect above has built the observer on the very first render —
  // which is why that effect observes whatever the refs have already stored rather than relying
  // on these to have done it.
  const scrollRef = useCallback((node: HTMLElement | null) => {
    if (box.current && observer.current) observer.current.unobserve(box.current);
    box.current = node;
    if (node && observer.current) observer.current.observe(node);
  }, []);

  const contentRef = useCallback((node: HTMLElement | null) => {
    if (content.current && observer.current) observer.current.unobserve(content.current);
    content.current = node;
    if (node && observer.current) observer.current.observe(node);
  }, []);

  /** One scroll event, read through `pinAfterScroll` — which is where the rule, and the two
   *  counter-examples that shaped it, are written down. */
  const onScroll = useCallback(() => {
    const node = box.current;
    if (!node) return;
    pinned.current = pinAfterScroll(
      node,
      { pinned: pinned.current, written: written.current },
      threshold,
    );
  }, [threshold]);

  /**
   * **A declaration, which is why it does not go through the rule.** Everywhere else the position
   * is evidence and the rule reads it; here the reader has pressed 送信 from wherever they had
   * scrolled to, and the rule would look at that position and correctly conclude they are
   * re-reading — undoing the one case where the intent is explicit. Writing `written` as well
   * leaves the two consistent, so every later reading agrees the transcript is at its end.
   */
  const pin = useCallback(() => {
    pinned.current = true;
    toBottom();
  }, [toBottom]);

  return { scrollRef, contentRef, onScroll, pin };
}
